import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  Plus, 
  Search, 
  Mail, 
  Phone, 
  CheckCircle, 
  X,
  Trash2,
  UserPlus,
  Calendar,
  MapPin,
  Download,
  Send,
  Link as LinkIcon,
  Unlink,
  Edit,
  DollarSign
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Add Attendee Modal
const AddAttendeeModal = ({ eventId, landingPages, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    main_event_id: eventId,
    name: "",
    email: "",
    phone: "",
    ticket_type: "Manual Entry",
    quantity: 1,
    notes: "",
    total_amount: 0,
    landing_page_slug: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/main-events/${eventId}/attendees`, formData);
      toast.success("Attendee added successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to add attendee");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            Add Attendee
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="(780) 555-1234"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Landing Page Source</label>
            <select
              value={formData.landing_page_slug}
              onChange={(e) => setFormData({ ...formData, landing_page_slug: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="">-- Select Landing Page --</option>
              {landingPages?.map(lp => (
                <option key={lp.slug} value={lp.slug}>
                  {lp.slug.toUpperCase().replace(/-/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Amount Paid (C$)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.total_amount}
              onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none resize-none"
              placeholder="VIP guest, special requirements, etc."
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? "Adding..." : "Add Attendee"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Edit Attendee Modal
const EditAttendeeModal = ({ attendee, landingPages, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: attendee?.name || "",
    email: attendee?.email || "",
    phone: attendee?.phone || "",
    ticket_type: attendee?.ticket_type || "Manual Entry",
    quantity: attendee?.quantity || 1,
    notes: attendee?.notes || "",
    total_amount: attendee?.total_amount || 0,
    landing_page_slug: attendee?.landing_page_slug || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/attendees/${attendee.id}`, formData);
      toast.success("Attendee updated successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update attendee");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            Edit Attendee
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Landing Page Source</label>
            <select
              value={formData.landing_page_slug}
              onChange={(e) => setFormData({ ...formData, landing_page_slug: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="">-- Select Landing Page --</option>
              {landingPages?.map(lp => (
                <option key={lp.slug} value={lp.slug}>
                  {lp.slug.toUpperCase().replace(/-/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Amount Paid (C$)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.total_amount}
              onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Create/Edit Main Event Modal
const EventModal = ({ event, landingPages, onClose, onSave }) => {
  const isEditing = !!event;
  const [formData, setFormData] = useState({
    name: event?.name || "",
    description: event?.description || "",
    date: event?.date || "",
    time: event?.time || "",
    location: event?.location || "",
    address: event?.address || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.date) {
      toast.error("Name and date are required");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/admin/main-events/${event.id}`, formData);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/main-events`, formData);
        toast.success("Event created");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to save event");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
            {isEditing ? "Edit Event" : "Create Main Event"}
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Event Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="SUDS ON THE VINE"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Date *</label>
              <input
                type="text"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
                placeholder="Saturday, May 9, 2026"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Time</label>
              <input
                type="text"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
                placeholder="6:30 PM"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="Blue Quill Community League"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              placeholder="11304 25 Ave NW, Edmonton, AB"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? "Saving..." : isEditing ? "Update Event" : "Create Event"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Link Landing Pages Modal
const LinkPagesModal = ({ event, allLandingPages, onClose, onSave }) => {
  const [linking, setLinking] = useState(null);
  
  const linkedPageIds = event.landing_pages?.map(lp => lp.id) || [];
  const unlinkedPages = allLandingPages.filter(lp => !lp.main_event_id || lp.main_event_id === event.id);

  const linkPage = async (pageId) => {
    setLinking(pageId);
    try {
      await axios.put(`${API}/admin/landing-pages/${pageId}/link-event/${event.id}`);
      toast.success("Landing page linked");
      onSave();
    } catch (error) {
      toast.error("Failed to link page");
    } finally {
      setLinking(null);
    }
  };

  const unlinkPage = async (pageId) => {
    setLinking(pageId);
    try {
      await axios.put(`${API}/admin/landing-pages/${pageId}/unlink-event`);
      toast.success("Landing page unlinked");
      onSave();
    } catch (error) {
      toast.error("Failed to unlink page");
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <div>
            <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Link Landing Pages
            </h2>
            <p className="text-sm text-[#A1A1AA] mt-1">{event.name}</p>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm text-[#A1A1AA]">Select landing pages to associate with this event:</p>
          
          {unlinkedPages.map(page => {
            const isLinked = linkedPageIds.includes(page.id);
            return (
              <div 
                key={page.id}
                className={`p-4 border rounded flex items-center justify-between ${
                  isLinked 
                    ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.05)]' 
                    : 'border-[#27272A] bg-[#121212]'
                }`}
              >
                <div>
                  <p className="text-[#F5F5F0] font-medium">{page.slug}</p>
                  <p className="text-xs text-[#A1A1AA]">{page.title?.slice(0, 50)}...</p>
                </div>
                <button
                  onClick={() => isLinked ? unlinkPage(page.id) : linkPage(page.id)}
                  disabled={linking === page.id}
                  className={`p-2 rounded transition-colors ${
                    isLinked
                      ? 'bg-red-900/30 text-red-500 hover:bg-red-900/50'
                      : 'bg-green-900/30 text-green-500 hover:bg-green-900/50'
                  }`}
                >
                  {linking === page.id ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isLinked ? (
                    <Unlink className="w-5 h-5" />
                  ) : (
                    <LinkIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="p-6 border-t border-[rgba(212,175,55,0.15)]">
          <button onClick={onClose} className="btn-primary w-full">Done</button>
        </div>
      </motion.div>
    </div>
  );
};

const GuestManagement = () => {
  const [mainEvents, setMainEvents] = useState([]);
  const [allLandingPages, setAllLandingPages] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modals
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [showLinkPages, setShowLinkPages] = useState(null);

  useEffect(() => {
    fetchMainEvents();
    fetchLandingPages();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchMainEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/main-events`);
      setMainEvents(response.data);
      if (response.data.length > 0 && !selectedEvent) {
        setSelectedEvent(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching main events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/event-landing-pages`);
      setAllLandingPages(response.data);
    } catch (error) {
      console.error("Error fetching landing pages:", error);
    }
  };

  const fetchAttendees = async (eventId) => {
    try {
      const response = await axios.get(`${API}/admin/main-events/${eventId}/attendees`);
      setAttendees(response.data.attendees);
      setEventDetails(response.data);
    } catch (error) {
      console.error("Error fetching attendees:", error);
    }
  };

  const toggleCheckIn = async (attendeeId, source) => {
    try {
      await axios.put(`${API}/admin/attendees/${attendeeId}/check-in?source=${source}`);
      fetchAttendees(selectedEvent);
    } catch (error) {
      toast.error("Failed to update check-in status");
    }
  };

  const deleteAttendee = async (attendeeId) => {
    if (!window.confirm("Delete this attendee?")) return;
    try {
      await axios.delete(`${API}/admin/attendees/${attendeeId}`);
      toast.success("Attendee deleted");
      fetchAttendees(selectedEvent);
    } catch (error) {
      toast.error("Failed to delete attendee");
    }
  };

  const exportAttendees = () => {
    const csv = [
      ["Ticket #", "Booking Code", "Name", "Email", "Phone", "Ticket Type", "Quantity", "Source", "Checked In"].join(","),
      ...attendees.map(a => [
        (a.id || "").substring(0, 8).toUpperCase(),
        a.booking_code || "",
        a.name,
        a.email,
        a.phone || "",
        a.ticket_type,
        a.quantity,
        a.source,
        a.checked_in ? "Yes" : "No"
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendees-${selectedEvent}.csv`;
    a.click();
  };

  const filteredAttendees = attendees.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.phone?.includes(searchTerm)
  );

  const currentEvent = mainEvents.find(e => e.id === selectedEvent);

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Guest & Attendee Management
            </h1>
            <p className="text-[#A1A1AA]">Manage attendees by event, add manual entries, and send communications</p>
          </div>
          <button
            onClick={() => { setEditingEvent(null); setShowEventModal(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Main Event
          </button>
        </div>

        {/* Main Events Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mainEvents.map(event => (
            <motion.div
              key={event.id}
              onClick={() => setSelectedEvent(event.id)}
              className={`p-6 border rounded-lg cursor-pointer transition-all ${
                selectedEvent === event.id
                  ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.05)]'
                  : 'border-[#27272A] bg-[#0F0F0F] hover:border-[rgba(212,175,55,0.3)]'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#F5F5F0]">{event.name}</h3>
                  <p className="text-sm text-[#A1A1AA]">{event.date}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowLinkPages(event); }}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#D4AF37]"
                    title="Link landing pages"
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingEvent(event); setShowEventModal(true); }}
                    className="p-1.5 text-[#A1A1AA] hover:text-[#D4AF37]"
                    title="Edit event"
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-3">
                <MapPin className="w-4 h-4" />
                {event.location}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#F5F5F0] font-bold">{event.total_attendees || 0}</span>
                  <span className="text-[#A1A1AA] text-sm">attendees</span>
                </div>
                <div className="text-xs text-[#A1A1AA]">
                  {event.landing_pages?.length || 0} landing page(s)
                </div>
              </div>
            </motion.div>
          ))}

          {mainEvents.length === 0 && !loading && (
            <div className="col-span-full text-center py-12 border border-dashed border-[#27272A] rounded-lg">
              <Users className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No main events yet</p>
              <button
                onClick={() => setShowEventModal(true)}
                className="btn-primary mt-4"
              >
                Create Your First Event
              </button>
            </div>
          )}
        </div>

        {/* Attendees Section */}
        {selectedEvent && currentEvent && (
          <div className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.15)] p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
                  {currentEvent.name} - Attendees
                </h2>
                {eventDetails?.landing_pages?.length > 0 && (
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    Linked pages: {eventDetails.landing_pages.map(lp => lp.slug).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={exportAttendees} className="btn-secondary flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowAddAttendee(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Attendee
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pl-10 focus:border-[#D4AF37] focus:outline-none"
              />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#121212] p-4 rounded border border-[#27272A]">
                <p className="text-2xl font-bold text-[#F5F5F0]">{attendees.length}</p>
                <p className="text-sm text-[#A1A1AA]">Total Attendees</p>
              </div>
              <div className="bg-[#121212] p-4 rounded border border-[#27272A]">
                <p className="text-2xl font-bold text-green-500">
                  {attendees.filter(a => a.checked_in).length}
                </p>
                <p className="text-sm text-[#A1A1AA]">Checked In</p>
              </div>
              <div className="bg-[#121212] p-4 rounded border border-[#27272A]">
                <p className="text-2xl font-bold text-[#D4AF37]">
                  {attendees.reduce((sum, a) => sum + (a.quantity || 1), 0)}
                </p>
                <p className="text-sm text-[#A1A1AA]">Total Tickets</p>
              </div>
            </div>

            {/* Attendees Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(212,175,55,0.15)] bg-[#121212]">
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Guest</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Ticket #</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Booking Code</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Ticket</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Source</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Check-In</th>
                    <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendees.map(attendee => (
                    <tr key={attendee.id} className="border-b border-[rgba(212,175,55,0.1)] hover:bg-[rgba(212,175,55,0.02)]">
                      <td className="py-3 px-4">
                        <p className="text-[#F5F5F0] font-medium">{attendee.name}</p>
                        <p className="text-[#A1A1AA] text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {attendee.email}
                        </p>
                        {attendee.phone && (
                          <p className="text-[#A1A1AA] text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {attendee.phone}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {attendee.id ? (
                          <span className="text-[#22C55E] font-mono text-sm font-bold" data-testid="attendee-ticket-number" title="Ticket # on PDF">
                            {(attendee.id || "").substring(0, 8).toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-[#6B7280] text-xs">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {attendee.booking_code ? (
                          <span className="text-[#D4AF37] font-mono text-sm">{attendee.booking_code}</span>
                        ) : (
                          <span className="text-[#6B7280] text-xs">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-[#D4AF37]">{attendee.ticket_type}</p>
                        <p className="text-[#A1A1AA] text-sm">Qty: {attendee.quantity}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-[#F5F5F0] font-medium flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-green-500" />
                          C${(attendee.total_amount || 0).toFixed(2)}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          attendee.source === 'manual' 
                            ? 'bg-purple-900/30 text-purple-400' 
                            : 'bg-blue-900/30 text-blue-400'
                        }`}>
                          {attendee.landing_page_display || (attendee.source === 'manual' ? 'Manual' : attendee.landing_page_slug?.toUpperCase())}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => toggleCheckIn(attendee.id, attendee.source)}
                          className={`p-2 rounded transition-colors ${
                            attendee.checked_in
                              ? 'bg-green-900/30 text-green-500'
                              : 'bg-[#27272A] text-[#A1A1AA] hover:text-[#F5F5F0]'
                          }`}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingAttendee(attendee)}
                            className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.location.href = `mailto:${attendee.email}`}
                            className="p-2 bg-[rgba(212,175,55,0.1)] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.2)] rounded"
                            title="Send email"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteAttendee(attendee.id)}
                            className="p-2 bg-red-900/30 text-red-500 hover:bg-red-900/50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredAttendees.length === 0 && (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-[#A1A1AA]">
                        No attendees found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddAttendee && selectedEvent && (
        <AddAttendeeModal
          eventId={selectedEvent}
          landingPages={eventDetails?.landing_pages || []}
          onClose={() => setShowAddAttendee(false)}
          onSave={() => fetchAttendees(selectedEvent)}
        />
      )}

      {editingAttendee && (
        <EditAttendeeModal
          attendee={editingAttendee}
          landingPages={eventDetails?.landing_pages || []}
          onClose={() => setEditingAttendee(null)}
          onSave={() => { fetchAttendees(selectedEvent); setEditingAttendee(null); }}
        />
      )}

      {showEventModal && (
        <EventModal
          event={editingEvent}
          landingPages={allLandingPages}
          onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
          onSave={() => { fetchMainEvents(); fetchLandingPages(); }}
        />
      )}

      {showLinkPages && (
        <LinkPagesModal
          event={showLinkPages}
          allLandingPages={allLandingPages}
          onClose={() => setShowLinkPages(null)}
          onSave={() => { fetchMainEvents(); fetchLandingPages(); }}
        />
      )}
    </AdminLayout>
  );
};

export default GuestManagement;
